import React, { useState, useEffect, useRef, useCallback } from "react";
import "./style.scss";

const MultiSelectDropdown = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options",
  style = {},
  id,
  name,
  onOpen,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: '100%', bottom: 'auto' });
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Initialize selected options from value prop
    if (Array.isArray(value)) {
      setSelectedOptions(value);
    } else if (typeof value === 'string' && value) {
      // Handle case where value might be a string
      setSelectedOptions([value]);
    } else {
      setSelectedOptions([]);
    }
  }, [value]);

  const calculateDropdownPosition = useCallback(() => {
    if (!dropdownRef.current) return;

    const rect = dropdownRef.current.getBoundingClientRect();
    const dropdownHeight = 200; // Approximate height of dropdown
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      // Not enough space below, but enough space above - position above
      setDropdownPosition({ top: 'auto', bottom: '100%' });
    } else {
      // Default position below
      setDropdownPosition({ top: '100%', bottom: 'auto' });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (onClose) onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const handleToggleDropdown = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (newIsOpen) {
      calculateDropdownPosition();
      if (onOpen) onOpen();
    } else {
      if (onClose) onClose();
    }
  };

  const handleOptionChange = (option) => {
    const newSelectedOptions = selectedOptions.includes(option)
      ? selectedOptions.filter((item) => item !== option)
      : [...selectedOptions, option];

    setSelectedOptions(newSelectedOptions);
    if (onChange) {
      onChange(newSelectedOptions);
    }
  };

  const handleSelectAll = () => {
    if (selectedOptions.length === options.length) {
      // Deselect all
      const newSelectedOptions = [];
      setSelectedOptions(newSelectedOptions);
      if (onChange) {
        onChange(newSelectedOptions);
      }
    } else {
      // Select all
      const newSelectedOptions = [...options];
      setSelectedOptions(newSelectedOptions);
      if (onChange) {
        onChange(newSelectedOptions);
      }
    }
  };

  const getDisplayText = () => {
    if (selectedOptions.length === 0) {
      return placeholder;
    } else if (selectedOptions.length === 1) {
      return selectedOptions[0];
    } else {
      return `${selectedOptions.length} options selected`;
    }
  };

  return (
    <div className="multi-select-dropdown" ref={dropdownRef} style={style}>
      <div 
        className="dropdown-header" 
        onClick={handleToggleDropdown}
        style={{
          opacity: selectedOptions.length === 0 && placeholder.toLowerCase() === "select an option" ? 0.5 : 1
        }}
      >
        <span>{getDisplayText()}</span>
        <span className="dropdown-arrow">{isOpen ? "▲" : "▼"}</span>
      </div>

      {isOpen && (
        <div 
          className="dropdown-content"
          style={{
            top: dropdownPosition.top,
            bottom: dropdownPosition.bottom
          }}
        >
          <div className="select-all-option" onClick={handleSelectAll}>
            {selectedOptions.length === options.length
              ? "De-select All"
              : "Select All"}
          </div>

          {options.map((option, index) => (
            <div key={index} className="option-item">
              <input
                type="checkbox"
                id={`${id}-option-${index}`}
                checked={selectedOptions.includes(option)}
                onChange={() => handleOptionChange(option)}
              />
              <label htmlFor={`${id}-option-${index}`}>{option}</label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
